import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useAuth } from '../../components/auth/AuthProvider';
import EventFeeDetailsTable from '../../components/events/EventFeeDetailsTable';
import EventSessionCards from '../../components/events/EventSessionCards';
import { downloadCertificate, fetchEventCheckoutClientSecret, finalizeEventRegistrationPayment, getAvailableEventById, listAvailableEvents, listMyRegistrations, registerToEvent } from '../../controllers/events';
import { stripeKey } from '../../controllers/config';

function SessionDetailsModal({ show, onHide, title, sessions, eventItem }) {
  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Sesiones de {title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column gap-3">
          <EventSessionCards sessions={sessions} bodyClassName="small" />

          <EventFeeDetailsTable eventItem={eventItem} />
        </div>
      </Modal.Body>
    </Modal>
  );
}

function parseCount(value, fallback = 0) {
  const parsed = parseInt(value);
  if (!Number.isFinite(parsed) || parsed < 0) {
    return fallback;
  }
  return parsed;
}

function normalizePeopleBreakdown(raw = {}) {
  const adults = parseCount(raw.adults, 0);
  const adolescents = parseCount(raw.adolescents, 0);
  const children = parseCount(raw.children, 0);
  return {
    adults,
    adolescents,
    children,
    total: adults + adolescents + children,
  };
}

function buildDefaultBreakdown(peopleCount = 1) {
  return {
    adults: parseCount(peopleCount, 1),
    adolescents: 0,
    children: 0,
  };
}

function EventLookupModal({ show, onHide, eventItem, registration, peopleCount, peopleBreakdown, onPeopleBreakdownChange, onRegister, busy }) {
  if (!eventItem) {
    return null;
  }

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>Evento #{eventItem.event_id}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column gap-3">
          <div>
            <h4 className="mb-1">{eventItem.title}</h4>
            <div className="text-muted">{eventItem.event_type}</div>
          </div>
          <Row className="g-2 small">
            <Col md={6}><strong>Inicio:</strong> {eventItem.starts_on || '-'}</Col>
            <Col md={6}><strong>Fin:</strong> {eventItem.ends_on || '-'}</Col>
            <Col md={6}><strong>Modalidad:</strong> {eventItem.modality || '-'}</Col>
            <Col md={6}><strong>Pago:</strong> {eventItem.payment_type || '-'}</Col>
            <Col md={6}><strong>Cupo:</strong> {eventItem.capacity_limit || '-'}</Col>
            <Col md={6}><strong>Conteo:</strong> {eventItem.registration_count_mode || '-'}</Col>
            <Col md={12}><strong>Descripcion:</strong> {eventItem.description || 'Sin descripcion'}</Col>
          </Row>

          <EventSessionCards sessions={eventItem.event_sessions} bodyClassName="small" />

          <EventFeeDetailsTable eventItem={eventItem} />

          {registration ? (
            <Alert variant="success" className="mb-0">Ya estas inscrito en este evento.</Alert>
          ) : (
            <div className="d-flex flex-column gap-2">
              {eventItem.registration_count_mode === 'people' && (
                <Row className="g-2">
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Adultos</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        value={peopleBreakdown?.adults ?? 1}
                        onChange={(e) => onPeopleBreakdownChange('adults', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Adolescentes</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        value={peopleBreakdown?.adolescents ?? 0}
                        onChange={(e) => onPeopleBreakdownChange('adolescents', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group>
                      <Form.Label>Ninos</Form.Label>
                      <Form.Control
                        type="number"
                        min={0}
                        value={peopleBreakdown?.children ?? 0}
                        onChange={(e) => onPeopleBreakdownChange('children', e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={12}>
                    <small className="text-muted">Total de personas: {peopleCount || 0}</small>
                  </Col>
                </Row>
              )}
              <Button disabled={busy} onClick={() => onRegister(eventItem)}>
                {busy ? 'Registrando...' : 'Inscribirme'}
              </Button>
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
}

function EventsMember() {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionKey, setActionKey] = useState('');
  const [error, setError] = useState('');
  const [okMessage, setOkMessage] = useState('');
  const [peopleBreakdownByEvent, setPeopleBreakdownByEvent] = useState({});
  const [cycleYear, setCycleYear] = useState(new Date().getFullYear());
  const [checkoutRegistrationId, setCheckoutRegistrationId] = useState(null);
  const [sessionDetails, setSessionDetails] = useState({ show: false, title: '', sessions: [], eventItem: null });
  const [searchEventId, setSearchEventId] = useState('');
  const [searchingEvent, setSearchingEvent] = useState(false);
  const [lookupEvent, setLookupEvent] = useState(null);
  const [showLookupModal, setShowLookupModal] = useState(false);

  const stripePromise = loadStripe(stripeKey);

  const registrationByEvent = useMemo(() => {
    const map = {};
    registrations.forEach((row) => {
      map[row.event_id || row.event?.event_id] = row;
    });
    return map;
  }, [registrations]);

  useEffect(() => {
    document.title = 'FAMA - Eventos';
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    setError('');
    try {
      const [availableEvents, myRows] = await Promise.all([
        listAvailableEvents(token, { cycleYear, statusFilter: 'upcoming' }),
        listMyRegistrations(token, { cycleYear }),
      ]);
      setEvents(Array.isArray(availableEvents) ? availableEvents : []);
      setRegistrations(Array.isArray(myRows) ? myRows : []);
    }
    catch (e) {
      setError(e.message || 'No se pudieron cargar eventos.');
    }
    finally {
      setLoading(false);
    }
  }

  async function onRegister(eventItem) {
    const key = `register-${eventItem.event_id}`;
    setActionKey(key);
    setError('');
    setOkMessage('');
    try {
      const payload = {};

      if (eventItem.registration_count_mode === 'people') {
        const rawBreakdown = peopleBreakdownByEvent[eventItem.event_id] || buildDefaultBreakdown(1);
        const normalizedBreakdown = normalizePeopleBreakdown(rawBreakdown);

        if (normalizedBreakdown.total < 1) {
          throw new Error('Captura al menos una persona para completar el registro.');
        }

        payload.peopleCount = normalizedBreakdown.total;
        payload.peopleBreakdown = {
          adults: normalizedBreakdown.adults,
          adolescents: normalizedBreakdown.adolescents,
          children: normalizedBreakdown.children,
        };
      }
      else {
        payload.peopleCount = 1;
      }

      await registerToEvent(token, eventItem.event_id, payload);
      setOkMessage('Registro completado.');
      setShowLookupModal(false);
      await loadData();
    }
    catch (e) {
      setError(e.message || 'No se pudo registrar al evento.');
    }
    finally {
      setActionKey('');
    }
  }

  function onPayWithStripe(registration) {
    setCheckoutRegistrationId(registration.event_registration_id);
    setError('');
    setOkMessage('');
  }

  function openSessionDetails(title, sessions, eventItem) {
    setSessionDetails({ show: true, title, sessions, eventItem: eventItem || null });
  }

  async function searchEventById() {
    if (!searchEventId) {
      setError('Captura un ID de evento.');
      return;
    }

    setSearchingEvent(true);
    setError('');
    setOkMessage('');

    try {
      const eventItem = await getAvailableEventById(token, searchEventId);
      setLookupEvent(eventItem);
      setShowLookupModal(true);
    }
    catch (e) {
      setError(e.message || 'No se pudo consultar el evento.');
    }
    finally {
      setSearchingEvent(false);
    }
  }

  async function onCheckoutComplete() {
    const registrationId = checkoutRegistrationId;
    setOkMessage('Pago recibido. Verificando estado con Stripe...');
    setError('');

    try {
      if (registrationId) {
        await finalizeEventRegistrationPayment(token, registrationId);
      }
      setOkMessage('Pago confirmado.');
    }
    catch (e) {
      setError(e.message || 'No se pudo confirmar el pago con Stripe.');
    }
    finally {
      setCheckoutRegistrationId(null);
      await loadData();
    }
  }

  async function onDownloadCertificate(registration) {
    const key = `cert-${registration.event_registration_id}`;
    setActionKey(key);
    setError('');
    setOkMessage('');

    try {
      await downloadCertificate(token, registration.event_registration_id);
      setOkMessage('Certificado descargado.');
      await loadData();
    }
    catch (e) {
      setError(e.message || 'No se pudo descargar certificado.');
    }
    finally {
      setActionKey('');
    }
  }

  return (
    <Container className="py-4">
      <Row className="mb-3 align-items-end">
        <Col>
          <h1 className="mb-1">Eventos</h1>
          <p className="text-muted mb-0">Inscribete a eventos disponibles y consulta tus registros.</p>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>Ciclo</Form.Label>
            <Form.Control type="number" value={cycleYear} onChange={e => setCycleYear(e.target.value)} />
          </Form.Group>
        </Col>
        <Col md="auto">
          <Button onClick={loadData}>Actualizar</Button>
        </Col>
      </Row>

      <Card className="mb-4">
        <Card.Header><strong>Buscar evento por ID</strong></Card.Header>
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Group controlId="member-event-search-id">
                <Form.Label>ID del evento</Form.Label>
                <Form.Control value={searchEventId} onChange={e => setSearchEventId(e.target.value)} placeholder="Ej. 123" />
              </Form.Group>
            </Col>
            <Col md="auto">
              <Button onClick={searchEventById} disabled={searchingEvent}>{searchingEvent ? 'Buscando...' : 'Buscar'}</Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {!!error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {!!okMessage && <Alert variant="success" dismissible onClose={() => setOkMessage('')}>{okMessage}</Alert>}

      <Card className="mb-4">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Eventos disponibles</strong>
          <Badge bg="secondary">{events.length}</Badge>
        </Card.Header>
        <Card.Body style={{ overflowX: 'auto' }}>
          {loading ? (
            <div className="text-center py-4"><Spinner /></div>
          ) : (
            <Table striped hover>
              <thead>
                <tr>
                  <th>Evento</th>
                  <th>Tipo</th>
                  <th>Inicio</th>
                  <th>Modalidad</th>
                  <th>Pago</th>
                  <th>Cupo</th>
                  <th>Acción</th>
                </tr>
              </thead>
              <tbody>
                {events.map((eventItem) => {
                  const myRegistration = registrationByEvent[eventItem.event_id];
                  const eventBreakdown = peopleBreakdownByEvent[eventItem.event_id]
                    || buildDefaultBreakdown(1);
                  const breakdownTotal = normalizePeopleBreakdown(eventBreakdown).total;
                  return (
                    <tr key={`event-open-${eventItem.event_id}`}>
                      <td>{eventItem.title}</td>
                      <td>{eventItem.event_type}</td>
                      <td>{eventItem.starts_on}</td>
                      <td>{eventItem.modality}</td>
                      <td>{eventItem.payment_type}</td>
                      <td>{eventItem.capacity_limit || '-'}</td>
                      <td>
                        {!myRegistration ? (
                          <div className="d-flex flex-column gap-2">
                            {eventItem.registration_count_mode === 'people' && (
                              <>
                                <small className="fw-semibold">Cantidad de personas</small>
                                <Row className="g-1">
                                  <Col xs={4}>
                                    <Form.Label className="mb-1 small">Adultos</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      min={0}
                                      value={eventBreakdown.adults}
                                      onChange={e => setPeopleBreakdownByEvent(prev => ({
                                        ...prev,
                                        [eventItem.event_id]: {
                                          ...eventBreakdown,
                                          adults: e.target.value,
                                        },
                                      }))}
                                    />
                                  </Col>
                                  <Col xs={4}>
                                    <Form.Label className="mb-1 small">Adolescentes</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      min={0}
                                      value={eventBreakdown.adolescents}
                                      onChange={e => setPeopleBreakdownByEvent(prev => ({
                                        ...prev,
                                        [eventItem.event_id]: {
                                          ...eventBreakdown,
                                          adolescents: e.target.value,
                                        },
                                      }))}
                                    />
                                  </Col>
                                  <Col xs={4}>
                                    <Form.Label className="mb-1 small">Niños</Form.Label>
                                    <Form.Control
                                      size="sm"
                                      type="number"
                                      min={0}
                                      value={eventBreakdown.children}
                                      onChange={e => setPeopleBreakdownByEvent(prev => ({
                                        ...prev,
                                        [eventItem.event_id]: {
                                          ...eventBreakdown,
                                          children: e.target.value,
                                        },
                                      }))}
                                    />
                                  </Col>
                                </Row>
                                <small className="text-muted">Total: {breakdownTotal}</small>
                              </>
                            )}
                            <Button
                              size="sm"
                              disabled={actionKey === `register-${eventItem.event_id}`}
                              onClick={() => onRegister(eventItem)}
                            >
                              Inscribirme
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                                onClick={() => openSessionDetails(eventItem.title, eventItem.event_sessions, eventItem)}
                            >
                              Ver detalles
                            </Button>
                          </div>
                        ) : (
                          <div className="d-flex flex-column gap-2 align-items-start">
                            <span className="text-success">Ya inscrito</span>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => openSessionDetails(eventItem.title, eventItem.event_sessions, eventItem)}
                            >
                              Ver detalles
                            </Button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {!events.length && (
                  <tr><td colSpan={7} className="text-center text-muted">No hay eventos disponibles.</td></tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>Mis inscripciones</strong>
          <Badge bg="secondary">{registrations.length}</Badge>
        </Card.Header>
        <Card.Body style={{ overflowX: 'auto' }}>
          {loading ? (
            <div className="text-center py-4"><Spinner /></div>
          ) : (
            <Table striped hover>
              <thead>
                <tr>
                  <th>Evento</th>
                  <th>Estado</th>
                  <th>Pago</th>
                  <th>Asistencia</th>
                  <th>Certificado</th>
                  <th>Sesiones</th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((registration) => {
                  const attended = (registration.event_attendances || []).filter(a => a.attended).length;
                  const totalSessions = registration.event?.event_sessions?.length || 0;
                  return (
                    <tr key={`my-reg-${registration.event_registration_id}`}>
                      <td>{registration.event?.title}</td>
                      <td>{registration.registration_status}</td>
                      <td>
                        <div className="d-flex flex-column gap-1">
                          <span>{registration.payment_status}</span>
                          {registration.event?.payment_type === 'stripe' && registration.payment_status !== 'pagado' && (
                            <Button
                              size="sm"
                              variant="outline-primary"
                              disabled={actionKey === `pay-${registration.event_registration_id}`}
                              onClick={() => onPayWithStripe(registration)}
                            >
                              Pagar
                            </Button>
                          )}
                        </div>
                      </td>
                      <td>{attended}/{totalSessions}</td>
                      <td>
                          {(registration.certificate_status === 'listo' || registration.certificate_status === 'emitido') ? (
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              disabled={actionKey === `cert-${registration.event_registration_id}`}
                              onClick={() => onDownloadCertificate(registration)}
                            >
                              PDF
                            </Button>
                          ) : (
                            <span className="text-muted">Pendiente</span>
                          )}
                      </td>
                      <td>
                        <Button
                          size="sm"
                          variant="outline-secondary"
                          onClick={() => openSessionDetails(registration.event?.title || 'evento', registration.event?.event_sessions, registration.event)}
                        >
                          Ver detalles
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {!registrations.length && (
                  <tr><td colSpan={6} className="text-center text-muted">Aun no tienes inscripciones.</td></tr>
                )}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      <Modal show={!!checkoutRegistrationId} onHide={() => setCheckoutRegistrationId(null)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Pago de evento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!!checkoutRegistrationId && (
            <EmbeddedCheckoutProvider
              stripe={stripePromise}
              options={{
                fetchClientSecret: fetchEventCheckoutClientSecret(checkoutRegistrationId, token),
                onComplete: onCheckoutComplete,
              }}
            >
              <EmbeddedCheckout />
            </EmbeddedCheckoutProvider>
          )}
        </Modal.Body>
      </Modal>

      <SessionDetailsModal
        show={sessionDetails.show}
        onHide={() => setSessionDetails({ show: false, title: '', sessions: [], eventItem: null })}
        title={sessionDetails.title}
        sessions={sessionDetails.sessions}
        eventItem={sessionDetails.eventItem}
      />

      <EventLookupModal
        show={showLookupModal}
        onHide={() => setShowLookupModal(false)}
        eventItem={lookupEvent}
        registration={lookupEvent ? registrationByEvent[lookupEvent.event_id] : null}
        peopleCount={lookupEvent ? normalizePeopleBreakdown(peopleBreakdownByEvent[lookupEvent.event_id] || buildDefaultBreakdown(1)).total : 1}
        peopleBreakdown={lookupEvent
          ? (peopleBreakdownByEvent[lookupEvent.event_id] || buildDefaultBreakdown(1))
          : buildDefaultBreakdown(1)}
        onPeopleBreakdownChange={(field, value) => {
          if (!lookupEvent) {
            return;
          }
          const current = peopleBreakdownByEvent[lookupEvent.event_id]
            || buildDefaultBreakdown(1);
          setPeopleBreakdownByEvent(prev => ({
            ...prev,
            [lookupEvent.event_id]: {
              ...current,
              [field]: value,
            },
          }));
        }}
        onRegister={onRegister}
        busy={lookupEvent ? actionKey === `register-${lookupEvent.event_id}` : false}
      />
    </Container>
  );
}

export default EventsMember;
